import React, { FC, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import isEmpty from 'lodash/isEmpty';
import map from 'lodash/map';
import {
  closeError,
  deleteSite,
  fetchProductionSites,
  resetProductionSites,
  getSites,
  getSitesLoaders,
  getSitesApiErrors,
  fetchSitesTimezones
} from 'features/Sites/sites';
import Snackbar from '@material-ui/core/Snackbar';
import Alert from '@material-ui/lab/Alert';
import CircularProgress from '@material-ui/core/CircularProgress';
import Grid from '@material-ui/core/Grid';
import IconButton from '@material-ui/core/IconButton';
import DeleteIcon from '@material-ui/icons/DeleteOutlineOutlined';
import EditIcon from '@material-ui/icons/EditOutlined';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import Skeleton from '@material-ui/lab/Skeleton';
import Card from '@material-ui/core/Card';
import CardHeader from '@material-ui/core/CardHeader';
import CardContent from '@material-ui/core/CardContent';
import CardActions from '@material-ui/core/CardActions';
import { Row } from 'components/UI/Row';
import { Column } from 'components/UI/Column';
import { StickyDiv } from 'components/UI/StickyDiv';
import { Divider } from 'components/UI/Divider';
import { SiteForm } from './SiteForm';
import { countries } from './countries';

const SitesList: FC<{}> = () => {
  const { t } = useTranslation(['translation', 'timezones']);
  const dispatch = useDispatch();
  const sites = useSelector(getSites);
  const { isLoading, isDeletingItems } = useSelector(getSitesLoaders);
  const { loadingError, deletingError } = useSelector(getSitesApiErrors);
  const [isOpenSiteForm, setIsOpenSiteForm] = useState(false);
  const [siteToEdit, setSiteToEdit] = useState();

  useEffect(() => {
    dispatch(fetchProductionSites(null));
    dispatch(fetchSitesTimezones(null));
    return () => {
      dispatch(resetProductionSites());
    };
  }, []);

  // TODO: FOR ALL THIS KIND OF ERRORS: MAKE CUSTOM HOOK
  if (loadingError) {
    return <Alert severity="error">{loadingError}</Alert>;
  }

  if (!isLoading && isEmpty(sites)) {
    return (
      <Column justifyContent="center" alignItems="center">
        <Typography style={{ marginBottom: '25px' }}>{t('sites.no site found')}</Typography>
        <Button variant="contained" color="primary" onClick={() => setIsOpenSiteForm(true)}>
          {t('sites.new site')}
        </Button>
        <SiteForm isOpen={isOpenSiteForm} closeFn={() => setIsOpenSiteForm(false)} />
      </Column>
    );
  }

  return (
    <>
      <StickyDiv>
        <div style={{ padding: '0 40px' }}>
          <Button variant="contained" color="primary" component="span" onClick={() => setIsOpenSiteForm(true)}>
            {t('sites.new site')}
          </Button>
        </div>
      </StickyDiv>
      <div style={{ height: '20px' }} />
      <Grid container spacing={2}>
        {map(isLoading ? Array.from(new Array(3)) : sites, (site: any, index: number) => (
          <Grid key={`site_${index}`} item xs={12} sm={6} md={4} lg={4} xl={2}>
            <Card variant="outlined">
              <CardHeader title={site ? site.name : <Skeleton variant="text" animation="wave" width="100%" />} />
              <Divider borderType="dashed" color="lightgrey" />
              <CardContent>
                <Row padding="10px 0">
                  {site ? (
                    <>
                      {t('sites.unique code')}: {site.code}
                    </>
                  ) : (
                    <Skeleton variant="text" animation="wave" width="100%" />
                  )}
                </Row>
                <Row padding="10px 0">
                  {site ? (
                    <>
                      {t('sites.country')}: {t('country.' + countries.find(c => c.value === site.country_code)?.value)}
                    </>
                  ) : (
                    <Skeleton variant="text" animation="wave" width="100%" />
                  )}
                </Row>
                <Row padding="10px 0">
                  {site ? (
                    <>
                      {t('sites.timezone')}: {site.time_zone ? t(site.time_zone, { ns: 'timezones' }) : '-'}
                    </>
                  ) : (
                    <Skeleton variant="text" animation="wave" width="100%" />
                  )}
                </Row>
                <div style={{ flex: 1 }} />
              </CardContent>
              <CardActions style={{ justifyContent: 'space-between' }}>
                <>
                  {site ? (
                    <>
                      <IconButton
                        aria-label="delete"
                        color="secondary"
                        disabled={isDeletingItems && isDeletingItems[site.id]}
                        onClick={() => {
                          dispatch(deleteSite(site.id));
                        }}
                      >
                        <DeleteIcon />
                        {isDeletingItems && isDeletingItems[site.id] && (
                          <CircularProgress
                            color="secondary"
                            size={34}
                            style={{
                              position: 'absolute',
                              top: '50%',
                              left: '50%',
                              marginTop: -17,
                              marginLeft: -17
                            }}
                          />
                        )}
                      </IconButton>
                      <IconButton
                        aria-label="edit"
                        color="primary"
                        onClick={() => {
                          setSiteToEdit(site);
                          setIsOpenSiteForm(true);
                        }}
                      >
                        <EditIcon />
                      </IconButton>
                    </>
                  ) : (
                    <>
                      <Skeleton
                        animation="wave"
                        variant="circle"
                        width={20}
                        height={20}
                        style={{ marginLeft: '15px' }}
                      />
                      <Skeleton
                        animation="wave"
                        variant="circle"
                        width={20}
                        height={20}
                        style={{ marginRight: '15px' }}
                      />
                    </>
                  )}
                </>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>
      <SiteForm
        isOpen={isOpenSiteForm}
        site={siteToEdit}
        closeFn={() => {
          setSiteToEdit(undefined);
          setIsOpenSiteForm(false);
        }}
      />
      <Snackbar
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        key="bottom,center"
        open={!isEmpty(deletingError)}
        autoHideDuration={5000}
        onClose={() => {
          dispatch(closeError());
        }}
      >
        <Alert severity="error" variant="filled" onClose={() => dispatch(closeError())}>
          {deletingError}
        </Alert>
      </Snackbar>
    </>
  );
};

export { SitesList };
